// ================================================
// CLARITY REPORTING — DASHBOARD PAGE
// ================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/common/Sidebar';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function DashboardPage() {
  const { user } = useAuth();
  const [websites, setWebsites]     = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  const [newUrl,   setNewUrl]       = useState('');
  const [newName,  setNewName]      = useState('');
  const [adding,   setAdding]       = useState(false);

  useEffect(() => { fetchWebsites(); }, []);

  async function fetchWebsites() {
    try {
      const { data } = await api.get('/websites');
      setWebsites(data);
    } catch (err) {
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddWebsite(e) {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post('/websites', { url: newUrl, display_name: newName });
      toast.success('Website added successfully');
      setNewUrl('');
      setNewName('');
      setShowAddWebsite(false);
      fetchWebsites();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add website');
    } finally {
      setAdding(false);
    }
  }

  async function handleRunReport(websiteId) {
    try {
      await api.post(`/websites/${websiteId}/run-report`);
      toast.success('Report generation started. We will email you when it is ready.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start report');
    }
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <main className="app-main">
        <div className="page-header">
          <div className="flex-between">
            <h1>Dashboard</h1>
            <button className="btn btn-primary" onClick={() => setShowAddWebsite(true)}>
              + Add Website
            </button>
          </div>
        </div>

        <div className="page-container">
          {/* Add website form */}
          {showAddWebsite && (
            <div className="card mb-32">
              <div className="card-header">
                <span className="card-title">Add Your Website</span>
                <button
                  onClick={() => setShowAddWebsite(false)}
                  style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleAddWebsite}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Website URL *</label>
                    <input
                      className="form-input"
                      placeholder="https://yourwebsite.com"
                      value={newUrl}
                      onChange={e => setNewUrl(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Display Name</label>
                    <input
                      className="form-input"
                      placeholder="My Website"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="submit" className="btn btn-primary" disabled={adding}>
                    {adding ? 'Adding...' : 'Add Website'}
                  </button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowAddWebsite(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Websites list */}
          {loading ? (
            <div className="loading">Loading websites&hellip;</div>
          ) : websites.length === 0 ? (
            <div className="empty-state">
              <h3>No websites yet</h3>
              <p>Add your website to start receiving weekly clarity reports.</p>
              <button className="btn btn-primary" onClick={() => setShowAddWebsite(true)}>
                Add Your First Website
              </button>
            </div>
          ) : (
            websites.map(website => (
              <WebsiteCard
                key={website.id}
                website={website}
                onRunReport={handleRunReport}
                onRefresh={fetchWebsites}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

// ---- Website Card Component ----
function WebsiteCard({ website, onRunReport, onRefresh }) {
  const { user } = useAuth();
  const [competitors,   setCompetitors]   = useState([]);
  const [reports,       setReports]       = useState([]);
  const [showReports,   setShowReports]   = useState(false);
  const [showAddComp,   setShowAddComp]   = useState(false);
  const [compUrl,       setCompUrl]       = useState('');
  const [compName,      setCompName]      = useState('');
  const [addingComp,    setAddingComp]    = useState(false);
  const [loadingReports,setLoadingReports]= useState(false);
  const [keywordLimit,  setKeywordLimit]  = useState(website.keyword_limit || 25);
  const [savingLimit,   setSavingLimit]   = useState(false);

  const isAgency = ['agency', 'agency_pro'].includes(user?.account_type);

  useEffect(() => { fetchCompetitors(); fetchReports(); }, [website.id]);

  async function handleKeywordLimitChange(newLimit) {
    setKeywordLimit(newLimit);
    setSavingLimit(true);
    try {
      await api.patch(`/websites/${website.id}/keyword-limit`, { keyword_limit: parseInt(newLimit) });
      toast.success(`Keyword limit set to ${newLimit}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update keyword limit');
    } finally {
      setSavingLimit(false);
    }
  }

  async function fetchCompetitors() {
    try {
      const { data } = await api.get(`/websites/${website.id}/competitors`);
      setCompetitors(data);
    } catch {}
  }

  async function fetchReports() {
    setLoadingReports(true);
    try {
      const { data } = await api.get(`/reports?websiteId=${website.id}&limit=10`);
      setReports(data.reports || []);
    } catch {} finally {
      setLoadingReports(false);
    }
  }

  async function handleAddCompetitor(e) {
    e.preventDefault();
    setAddingComp(true);
    try {
      await api.post(`/websites/${website.id}/competitors`, {
        url: compUrl, display_name: compName
      });
      toast.success('Competitor added');
      setCompUrl('');
      setCompName('');
      setShowAddComp(false);
      fetchCompetitors();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add competitor');
    } finally {
      setAddingComp(false);
    }
  }

  async function handleRemoveCompetitor(competitorId) {
    try {
      await api.delete(`/websites/${website.id}/competitors/${competitorId}`);
      toast.success('Competitor removed');
      fetchCompetitors();
    } catch {
      toast.error('Failed to remove competitor');
    }
  }

  return (
    <div className="card">
      {/* Website header */}
      <div className="card-header">
        <div>
          <span className="card-title">{website.display_name || website.domain}</span>
          {website.client_type === 'prospect' && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', background: '#fff0f0', color: '#CC0000', border: '1px solid #CC0000', padding: '2px 8px', marginLeft: 10 }}>Prospect</span>
          )}
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginTop: 4 }}>
            {website.domain}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Keyword limit selector — agency only */}
          {isAgency && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1,
                textTransform: 'uppercase', color: 'var(--color-gray-500)'
              }}>
                Keywords
              </label>
              <select
                className="form-select"
                style={{ padding: '4px 8px', fontSize: 12, width: 'auto' }}
                value={keywordLimit}
                onChange={e => handleKeywordLimitChange(e.target.value)}
                disabled={savingLimit}
              >
                {[25, 50, 75, 100, 150, 200].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onRunReport(website.id)}
          >
            Run Report Now
          </button>
        </div>
      </div>

      {/* Report History */}
      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Report History</span>
        {reports.length > 0 && (
          <button
            onClick={() => setShowReports(!showReports)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 700, letterSpacing: 1,
              textTransform: 'uppercase', color: 'var(--color-gray-500)'
            }}
          >
            {showReports ? 'Hide' : `Show ${reports.length}`}
          </button>
        )}
      </div>

      {loadingReports ? (
        <p style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 16 }}>Loading...</p>
      ) : reports.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 16 }}>
          No reports yet. Click "Run Report Now" to generate your first report.
        </p>
      ) : (
        <>
          {/* Always show latest report */}
          <ReportRow report={reports[0]} isLatest={true} />

          {/* Show rest when expanded */}
          {showReports && reports.slice(1).map(report => (
            <ReportRow key={report.id} report={report} />
          ))}

          {!showReports && reports.length > 1 && (
            <button
              onClick={() => setShowReports(true)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: 'var(--color-gray-500)',
                padding: '4px 0', marginBottom: 16, textDecoration: 'underline'
              }}
            >
              + {reports.length - 1} older report{reports.length > 2 ? 's' : ''}
            </button>
          )}
        </>
      )}

      {/* Competitors */}
      <div className="section-title">Competitor Sites</div>

      {competitors.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 16 }}>
          No competitors added yet. Add up to 3 competitor sites to compare keywords.
        </p>
      ) : (
        <table className="data-table mb-16">
          <thead>
            <tr>
              <th>Competitor</th>
              <th>Domain</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {competitors.map(comp => (
              <tr key={comp.id}>
                <td>{comp.display_name || comp.domain}</td>
                <td className="text-mono text-small">{comp.domain}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveCompetitor(comp.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add competitor form */}
      {!showAddComp && competitors.length < 3 && (
        <button
          className="btn btn-outline btn-sm"
          onClick={() => setShowAddComp(true)}
        >
          + Add Competitor
        </button>
      )}

      {showAddComp && (
        <form onSubmit={handleAddCompetitor} style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
            <div>
              <label className="form-label">Competitor URL *</label>
              <input
                className="form-input"
                placeholder="competitor.com"
                value={compUrl}
                onChange={e => setCompUrl(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="form-label">Display Name</label>
              <input
                className="form-input"
                placeholder="Competitor Name"
                value={compName}
                onChange={e => setCompName(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={addingComp}>
              {addingComp ? '...' : 'Add'}
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setShowAddComp(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Prospect Action Plan Section */}
      {website.client_type === 'prospect' && <ProspectSection website={website} onConvert={onRefresh} />}

      {/* Manual Keywords Section */}
      <KeywordsSection websiteId={website.id} />

      {/* SEO Health Audit Section */}
      <AuditSection websiteId={website.id} domain={website.domain} />

    </div>
  );
}

// ---- SEO Audit Section Component ----
function AuditSection({ websiteId, domain }) {
  const [audit,    setAudit]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [running,  setRunning]  = useState(false);

  useEffect(() => { fetchAudit(); }, []);

  async function fetchAudit() {
    try {
      const { data } = await api.get(`/websites/${websiteId}/audit`);
      setAudit(data);
    } catch {}
  }

  async function handleRunAudit() {
    setRunning(true);
    try {
      await api.post(`/websites/${websiteId}/audit/run`);
      toast.success('SEO audit started — results will appear shortly.');
      // Poll for results after 45 seconds
      setTimeout(() => {
        fetchAudit();
        setRunning(false);
      }, 45000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start audit');
      setRunning(false);
    }
  }

  const scoreColor = audit ? (
    audit.overall_score >= 90 ? '#1a7a1a' :
    audit.overall_score >= 75 ? '#2d7dd2' :
    audit.overall_score >= 60 ? '#e8a020' : '#CC0000'
  ) : '#888';

  const scoreLabel = audit ? (
    audit.overall_score >= 90 ? 'Excellent' :
    audit.overall_score >= 75 ? 'Good' :
    audit.overall_score >= 60 ? 'Fair' :
    audit.overall_score >= 40 ? 'Poor' : 'Critical'
  ) : '';

  return (
    <div style={{ marginTop: 24 }}>
      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>SEO Health</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {audit && (
            <button
              onClick={() => setExpanded(!expanded)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-gray-500)' }}
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          )}
          <button
            className="btn btn-outline btn-sm"
            onClick={handleRunAudit}
            disabled={running}
          >
            {running ? 'Running...' : 'Run Audit'}
          </button>
        </div>
      </div>

      {!audit ? (
        <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 16 }}>
          No audit yet. Click "Run Audit" to check your site's SEO health.
        </p>
      ) : (
        <>
          {/* Score summary */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: expanded ? 20 : 16 }}>
            {/* Big score */}
            <div style={{ textAlign: 'center', minWidth: 80 }}>
              <div style={{ fontSize: 42, fontWeight: 700, color: scoreColor, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
                {audit.overall_score}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: scoreColor }}>
                {scoreLabel}
              </div>
            </div>

            {/* Category bars */}
            <div style={{ flex: 1 }}>
              {[
                { label: 'Technical', score: audit.technical_score },
                { label: 'On-Page',   score: audit.onpage_score },
                { label: 'Content',   score: audit.content_score },
                { label: 'Authority', score: audit.authority_score }
              ].map(cat => (
                <div key={cat.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 64, fontSize: 11, color: 'var(--color-gray-500)', flexShrink: 0 }}>{cat.label}</div>
                  <div style={{ flex: 1, height: 6, background: 'var(--color-gray-100)', position: 'relative' }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${cat.score}%`,
                      background: cat.score >= 75 ? '#1a7a1a' : cat.score >= 50 ? '#e8a020' : '#CC0000',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                  <div style={{ width: 32, fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)', textAlign: 'right', flexShrink: 0 }}>
                    {cat.score}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Audit meta */}
          <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginBottom: expanded ? 16 : 0 }}>
            {audit.pages_audited} pages audited · {audit.total_issues} issues found
            {audit.high_issues > 0 && <span style={{ color: 'var(--color-problem-text)', fontWeight: 700, marginLeft: 8 }}>({audit.high_issues} critical)</span>}
            · {new Date(audit.audit_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>

          {/* Expanded: Quick wins + top issues */}
          {expanded && (
            <div>
              {audit.quick_wins?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-gray-700)', marginBottom: 8 }}>
                    Quick Wins
                  </div>
                  {audit.quick_wins.map((win, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--color-gray-700)', marginBottom: 6, padding: '6px 10px', background: 'var(--color-concern-bg)', borderLeft: '3px solid var(--color-concern-border)' }}>
                      <span style={{ flexShrink: 0, fontWeight: 700 }}>{i + 1}.</span>
                      {win}
                    </div>
                  ))}
                </div>
              )}

              {audit.top_issues?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-gray-700)', marginBottom: 8 }}>
                    All Issues
                  </div>
                  {audit.top_issues.map((issue, i) => (
                    <div key={i} style={{
                      fontSize: 12, marginBottom: 4, padding: '5px 10px',
                      borderLeft: `3px solid ${issue.severity === 'high' ? 'var(--color-problem-text)' : issue.severity === 'medium' ? 'var(--color-concern-border)' : 'var(--color-gray-300)'}`,
                      background: issue.severity === 'high' ? 'var(--color-problem-bg)' : issue.severity === 'medium' ? 'var(--color-concern-bg)' : 'var(--color-gray-50)',
                      color: issue.severity === 'high' ? 'var(--color-problem-text)' : 'var(--color-gray-700)'
                    }}>
                      {issue.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- Keywords Section Component ----
function KeywordsSection({ websiteId }) {
  const [keywords,     setKeywords]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [expanded,     setExpanded]     = useState(false);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [newKeyword,   setNewKeyword]   = useState('');
  const [fetchVolume,  setFetchVolume]  = useState(false);
  const [adding,       setAdding]       = useState(false);

  useEffect(() => {
    if (expanded) fetchKeywords();
  }, [expanded]);

  async function fetchKeywords() {
    setLoading(true);
    try {
      const { data } = await api.get(`/websites/${websiteId}/keywords`);
      setKeywords(data);
    } catch {
      toast.error('Failed to load keywords');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddKeyword(e) {
    e.preventDefault();
    if (!newKeyword.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/websites/${websiteId}/keywords`, {
        keyword: newKeyword.trim(),
        fetch_volume: fetchVolume
      });
      toast.success(data.message);
      setNewKeyword('');
      setFetchVolume(false);
      setShowAddForm(false);
      fetchKeywords();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add keyword');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemoveKeyword(keywordId, keyword) {
    try {
      await api.delete(`/websites/${websiteId}/keywords/${keywordId}`);
      toast.success(`"${keyword}" removed`);
      setKeywords(prev => prev.filter(k => k.id !== keywordId));
    } catch {
      toast.error('Failed to remove keyword');
    }
  }

  const manualKeywords = keywords.filter(k => k.source === 'manual');
  const autoKeywords   = keywords.filter(k => k.source !== 'manual');

  return (
    <div style={{ marginTop: 24 }}>
      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Tracked Keywords</span>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, fontWeight: 700, letterSpacing: 1,
            textTransform: 'uppercase', color: 'var(--color-gray-500)'
          }}
        >
          {expanded ? 'Hide' : `Manage`}
        </button>
      </div>

      {!expanded && (
        <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 16 }}>
          {manualKeywords.length > 0
            ? `${manualKeywords.length} manual keyword${manualKeywords.length > 1 ? 's' : ''} added`
            : 'Add keywords you want to track regardless of automatic rankings.'
          }
        </p>
      )}

      {expanded && (
        <div>
          {loading ? (
            <p style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Loading...</p>
          ) : (
            <>
              {/* Manual keywords */}
              {manualKeywords.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-gray-500)', marginBottom: 8 }}>
                    Manual Keywords ({manualKeywords.length})
                  </div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Keyword</th>
                        <th style={{ textAlign: 'center' }}>Current Rank</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualKeywords.map(kw => (
                        <tr key={kw.id}>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{kw.keyword}</td>
                          <td style={{ textAlign: 'center' }}>
                            {kw.position
                              ? <span className="rank-badge">{kw.position}</span>
                              : <span style={{ color: 'var(--color-gray-300)', fontSize: 12 }}>Not ranked</span>
                            }
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveKeyword(kw.id, kw.keyword)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Auto-detected keywords summary */}
              {autoKeywords.length > 0 && (
                <p style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 16 }}>
                  + {autoKeywords.length} keywords auto-detected from {keywords[0]?.source === 'google_search_console' ? 'Google Search Console' : 'DataForSEO'}
                </p>
              )}

              {/* Add keyword form */}
              {!showAddForm ? (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowAddForm(true)}
                  style={{ marginTop: 8 }}
                >
                  + Add Keyword
                </button>
              ) : (
                <form onSubmit={handleAddKeyword} style={{ marginTop: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'end', marginBottom: 8 }}>
                    <div>
                      <label className="form-label">Keyword to track</label>
                      <input
                        className="form-input"
                        placeholder="e.g. custom car show boards"
                        value={newKeyword}
                        onChange={e => setNewKeyword(e.target.value)}
                        autoFocus
                        required
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={adding}>
                      {adding ? '...' : 'Add'}
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowAddForm(false); setNewKeyword(''); }}>
                      Cancel
                    </button>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-gray-500)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={fetchVolume}
                      onChange={e => setFetchVolume(e.target.checked)}
                    />
                    Look up search volume (uses 1 DataForSEO credit)
                  </label>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ---- Prospect Section Component ----
function ProspectSection({ website, onConvert }) {
  const [plan, setPlan]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [converting, setConverting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { fetchPlan(); }, []);

  async function fetchPlan() {
    try {
      const { data } = await api.get(`/websites/${website.id}/action-plan`);
      setPlan(data);
    } catch {}
  }

  async function handleGenerate() {
    setRunning(true);
    try {
      await api.post(`/websites/${website.id}/action-plan/generate`);
      toast.success('Action plan generating  check back in about 60 seconds.');
      setTimeout(() => { fetchPlan(); setRunning(false); }, 65000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start action plan');
      setRunning(false);
    }
  }

  async function handleConvertToClient() {
    if (!window.confirm(`Convert ${website.domain} to a client? Weekly reports will begin next scheduled run.`)) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/websites/${website.id}/convert-to-client`);
      toast.success(data.message);
      onConvert();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to convert');
      setConverting(false);
    }
  }

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: '2px solid #CC0000' }}>
      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#CC0000' }}>
        <span>SEO Action Plan</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {plan && (
            <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-gray-500)' }}>
              {expanded ? 'Hide' : 'View'}
            </button>
          )}
          <button className="btn btn-sm" style={{ background: '#CC0000', color: '#fff', border: 'none', cursor: 'pointer', padding: '4px 12px', fontSize: 11, fontWeight: 700 }} onClick={handleGenerate} disabled={running}>
            {running ? 'Generating...' : plan ? 'Regenerate' : 'Generate Plan'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleConvertToClient} disabled={converting}>
            {converting ? 'Converting...' : 'Convert to Client'}
          </button>
        </div>
      </div>

      {!plan ? (
        <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 16 }}>
          Generate a one-time SEO analysis for this prospect. Includes keyword rankings, competitor gaps, SEO health score, and recommended actions.
        </p>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 12 }}>
            Generated {new Date(plan.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            &nbsp;&bull;&nbsp; {plan.summary?.total_keywords || 0} keywords
            &nbsp;&bull;&nbsp; {plan.summary?.page1_count || 0} on page 1
            &nbsp;&bull;&nbsp; {plan.summary?.opportunity_count || 0} opportunities
            {plan.summary?.seo_score && <span>&nbsp;&bull;&nbsp; SEO score: <strong>{plan.summary.seo_score}/100</strong></span>}
          </div>

          {expanded && (
            <div>
              {/* Recommended Actions */}
              {plan.actions?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-gray-700)', marginBottom: 10 }}>Recommended Actions</div>
                  {plan.actions.map((action, i) => (
                    <div key={i} style={{ marginBottom: 8, padding: '12px 14px', background: action.priority === 'high' ? '#fff0f0' : '#fafafa', borderLeft: `3px solid ${action.priority === 'high' ? '#CC0000' : '#e0e0e0'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{action.category}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: action.priority === 'high' ? '#CC0000' : '#888' }}>{action.priority}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--color-gray-700)', marginBottom: 4 }}>{action.action}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-gray-500)', fontStyle: 'italic' }}>{action.impact}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Top Keywords */}
              {plan.rankings?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-gray-700)', marginBottom: 8 }}>Top Keyword Rankings</div>
                  <table className="data-table">
                    <thead><tr><th>Keyword</th><th style={{ textAlign: 'center' }}>Rank</th></tr></thead>
                    <tbody>
                      {plan.rankings.slice(0, 10).map((kw, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{kw.keyword}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="rank-badge" style={{ background: kw.position <= 10 ? 'var(--color-success-bg)' : 'var(--color-gray-50)' }}>#{kw.position}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Top Opportunities */}
              {plan.opportunities?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-gray-700)', marginBottom: 8 }}>Growth Opportunities</div>
                  <table className="data-table">
                    <thead><tr><th>Keyword</th><th style={{ textAlign: 'right' }}>Searches/mo</th><th>Competitor</th></tr></thead>
                    <tbody>
                      {plan.opportunities.slice(0, 6).map((op, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{op.keyword}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{op.search_volume?.toLocaleString() || '-'}</td>
                          <td style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>{op.found_on_competitor}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Report Row Component ----
function ReportRow({ report, isLatest }) {
  const date = new Date(report.report_date).toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });

  const hasProblems = report.keywords_problem > 0;
  const hasConcerns = report.keywords_concern > 0;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 0',
      borderBottom: '1px solid var(--color-gray-100)',
      marginBottom: 4
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: isLatest ? 600 : 400 }}>
            {date}
            {isLatest && (
              <span style={{
                marginLeft: 8, fontSize: 10, fontWeight: 700,
                letterSpacing: 1, textTransform: 'uppercase',
                color: 'var(--color-gray-500)'
              }}>
                Latest
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginTop: 2 }}>
            {report.total_keywords} keywords
            {report.keywords_improved > 0 && (
              <span style={{ color: 'var(--color-success-text)', marginLeft: 8 }}>
                ▲ {report.keywords_improved}
              </span>
            )}
            {hasProblems && (
              <span style={{ color: 'var(--color-problem-text)', marginLeft: 8, fontWeight: 700 }}>
                {report.keywords_problem} problem{report.keywords_problem > 1 ? 's' : ''}
              </span>
            )}
            {!hasProblems && hasConcerns && (
              <span style={{ color: 'var(--color-concern-text)', marginLeft: 8 }}>
                {report.keywords_concern} concern{report.keywords_concern > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      <a
        href={`/reports/${report.id}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 1,
          textTransform: 'uppercase', color: 'var(--color-black)',
          textDecoration: 'none', padding: '5px 12px',
          border: '1px solid var(--color-gray-300)',
          whiteSpace: 'nowrap'
        }}
      >
        Open ↗
      </a>
    </div>
  );
}
